import React, { memo, useCallback, useMemo } from "react";
import {
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useUsers } from "modules/providers/users-provider";
import colors from "app/colors";

import UserAvatar from "modules/_shared/user-avatar";
import {
  InfoIcon,
  ManageAccountsIcon,
  GearIcon,
  PersonCircleIcon,
  AdminUserTypeIcon,
  PencilIcon,
} from "modules/icons";
import { UserRoleType } from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import AdminEditUserCallout from "./admin-user/admin-edit-user-callout";
import EmailAddress from "modules/_shared/email-address";
import AdminEditUserPhone from "./admin-user/admin-edit-user-phone";
import AdminEditUserHidePhone from "./admin-user/admin-edit-user-hide-phone";
import AdminEditUserHideEmail from "./admin-user/admin-edit-user-hide-email";
import AdminEditUserEmail from "./admin-user/admin-edit-user-email";
import AdminEditUserActive from "./admin-user/admin-edit-user-active";
import AdminEditUserLanguage from "./admin-user/admin-edit-user-language";
import AdminEditUserSms from "./admin-user/admin-edit-user-sms";
import AdminUserDistributionGroups from "./admin-user/admin-user-distribution-groups";
import AdminEditUserName from "./admin-user/admin-edit-user-name";
import UserAttributes from "../drawers/components/user-profile/user-attributes";
import UserOnlineStatus from "modules/drawers/components/user-profile/user-online-status";
import AdminEditUserAdmin from "./admin-user/admin-edit-user-admin";
import AdminEditUserAllDistributionGroups from "./admin-user/admin-edit-user-all-distribution-groups";

interface IProps {
  userId: string;
}

const AdminUserDrawer = ({ userId }: IProps) => {
  const { openModal } = useOverlay();
  const { usersById } = useUsers();

  const onEditUserAttributesClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.ADMIN_EDIT_USER_ATTRIBUTES,
      id: userId,
    });
  }, [openModal, userId]);

  const user = useMemo(() => usersById[userId], [userId, usersById]);

  if (!user) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton left={8} top={8} color={colors.gray30} size="lg" />
      <DrawerHeader pt={8}>
        <Center fontFamily="Outfit" fontSize={26} fontWeight={700}>
          User Profile
        </Center>
      </DrawerHeader>
      <DrawerBody p={0}>
        <Stack spacing={8} px={8}>
          <VStack spacing={4} w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <InfoIcon boxSize={5} />
              <Text fontWeight={600} fontSize={18}>
                User Info
              </Text>
            </HStack>
            <HStack w="full" spacing={4}>
              <UserAvatar
                userId={user.id}
                size="xl"
                displayOnlineIndicator={false}
              />
              <VStack w="full" alignItems="flex-start">
                <AdminEditUserName user={user} />
                {user.roleType === UserRoleType.Admin && (
                  <AdminUserTypeIcon fill={colors.gray50} />
                )}
                <UserOnlineStatus user={user} />
              </VStack>
            </HStack>
            <VStack
              w="full"
              alignItems="flex-start"
              divider={<Divider />}
              spacing={0}
            >
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Username
                </Text>
                <AdminEditUserCallout user={user} />
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Synco Email
                </Text>
                <EmailAddress id={user.id} fontSize="14" truncate />
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Mobile
                </Text>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <AdminEditUserPhone user={user} />
                  <AdminEditUserHidePhone user={user} />
                </VStack>
              </HStack>
              <HStack spacing={0} w="full" minH="48px">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Email
                </Text>
                <VStack w="full" alignItems="flex-start" spacing={0}>
                  <AdminEditUserEmail user={user} />
                  <AdminEditUserHideEmail user={user} />
                </VStack>
              </HStack>
            </VStack>
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <HStack w="full">
                <PersonCircleIcon boxSize={6} />
                <Text fontWeight={600} fontSize={18}>
                  User Attributes
                </Text>
                <Spacer />
                <Button
                  aria-label="Edit user attributes"
                  leftIcon={
                    <PencilIcon boxSize={4} fill={colors.orangePrimary} />
                  }
                  color={colors.orangePrimary}
                  variant="ghost"
                  fontSize={14}
                  fontWeight={600}
                  size="xxs"
                  onClick={onEditUserAttributesClicked}
                >
                  Edit
                </Button>
              </HStack>
              <Divider borderColor={colors.black} />
              <UserAttributes user={user} />
            </VStack>
            <AdminEditUserActive user={user} />
          </VStack>

          <VStack w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <GearIcon boxSize={5} />
              <Text fontWeight={600} fontSize={18}>
                Settings
              </Text>
            </HStack>
            <HStack spacing={0} w="full">
              <Text
                minW={140}
                color={colors.blackSecondary}
                fontWeight={700}
                fontSize={14}
              >
                Language
              </Text>
              <AdminEditUserLanguage user={user} />
            </HStack>
            <Divider />
            <HStack spacing={0} w="full" py={2}>
              <Text
                minW={140}
                color={colors.blackSecondary}
                fontWeight={700}
                fontSize={14}
              >
                SMS Delivery
              </Text>
              <Text color={colors.gray50} fontWeight={500} fontSize={14}>
                Deliver messages via SMS
              </Text>
              <Spacer />
              <AdminEditUserSms user={user} />
            </HStack>
          </VStack>

          <VStack w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <ManageAccountsIcon boxSize={5} />
              <Text fontWeight={600} fontSize={18}>
                Advanced Settings
              </Text>
            </HStack>
            <VStack spacing={0} w="full" alignItems="flex-start">
              <HStack spacing={0} w="full">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Make an Admin
                </Text>
                <Spacer />
                <AdminEditUserAdmin user={user} />
              </HStack>
              <Text fontSize={14} fontWeight={400} color={colors.grayMain}>
                Admins can add and edit users, groups, and tags
              </Text>
            </VStack>
            <Divider />
            <VStack spacing={0} w="full" alignItems="flex-start">
              <HStack spacing={0} w="full">
                <Text
                  minW={140}
                  color={colors.blackSecondary}
                  fontWeight={700}
                  fontSize={14}
                >
                  Add to all Groups
                </Text>
                <Spacer />
                <AdminEditUserAllDistributionGroups user={user} />
              </HStack>
              <Text fontSize={14} fontWeight={400} color={colors.grayMain}>
                Automatically add this user to every group in org
              </Text>
            </VStack>
          </VStack>

          <AdminUserDistributionGroups user={user} />
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(AdminUserDrawer);
